import type { BindingTarget, PromptBinding, PromptContent, PromptSource, ReferenceKind } from "./prompt-content.js";
import { canvasDocumentV3Schema, getPromptContent, type CanvasDocumentV3, type CanvasNodeV3 } from "./studio-v3.js";

export type CanvasReferenceCommandV3 =
  | { type: "delete_nodes"; nodeIds: readonly string[] }
  | { type: "delete_edges"; edgeIds: readonly string[] }
  | { type: "reorder_references"; targetNodeId: string; orderedEdgeIds: readonly string[] }
  | { type: "set_prompt"; source: PromptSource; content: PromptContent }
  | { type: "bind_reference"; targetNodeId: string; source: PromptSource; slotId: string; mediaEdgeId: string }
  | { type: "clear_binding"; targetNodeId: string; source: PromptSource; slotId: string }
  | { type: "remove_occurrence"; source: PromptSource; segmentIndex: number }
  | { type: "replace_media"; nodeId: string; assetId: string };

const isMedia = (node: CanvasNodeV3 | undefined): node is Extract<CanvasNodeV3, { kind: ReferenceKind }> =>
  node?.kind === "image" || node?.kind === "video" || node?.kind === "audio";
const sameSource = (a: PromptSource, b: PromptSource): boolean =>
  a.nodeId === b.nodeId && a.field === b.field && a.inputEdgeId === b.inputEdgeId;
const previousIdentity = (binding: PromptBinding | undefined): BindingTarget | undefined =>
  binding?.value.state === "bound" ? binding.value.target : binding?.value.previous;

function requireTarget(document: CanvasDocumentV3, id: string) {
  const node = document.nodes.find((node) => node.id === id);
  if (node?.kind !== "seedance") throw new Error("引用目标生成节点不存在。");
  return node;
}

function setPrompt(document: CanvasDocumentV3, source: PromptSource, content: PromptContent): CanvasDocumentV3 {
  const before = getPromptContent(document, source);
  const oldKinds = new Map(before.segments.flatMap((segment) => segment.type === "reference" ? [[segment.slotId, segment.kind] as const] : []));
  const slots = new Set<string>();
  for (const segment of content.segments) {
    if (segment.type !== "reference") continue;
    const kind = oldKinds.get(segment.slotId);
    if (kind !== undefined && kind !== segment.kind) throw new Error("已有引用槽位不能更改媒体类型，请新建引用槽位。");
    slots.add(segment.slotId);
  }
  return { ...document, nodes: document.nodes.map((node) => {
    let updated = node;
    if (node.id === source.nodeId) {
      if (node.kind === "seedance" && source.field === "prompt") updated = { ...node, settings: { ...node.settings, prompt: content } };
      if (node.kind === "text" && source.field === "text") updated = { ...node, text: content };
      if (node.kind === "script" && (source.field === "title" || source.field === "synopsis")) updated = { ...node, [source.field]: content };
    }
    if (updated.kind !== "seedance") return updated;
    // Membership is shared by node/field, but each consumer keeps its own values.
    // New slots deliberately have no inferred binding (the resolver reports unbound).
    return { ...updated, referenceBindings: updated.referenceBindings.filter((binding) =>
      binding.source.nodeId !== source.nodeId || binding.source.field !== source.field || slots.has(binding.slotId)) };
  }) };
}

function deleteGraph(document: CanvasDocumentV3, removedNodes: Set<string>, removedEdges: Set<string>): CanvasDocumentV3 {
  for (const edge of document.edges) {
    if (removedNodes.has(edge.sourceNodeId) || removedNodes.has(edge.targetNodeId)) removedEdges.add(edge.id);
  }
  const nodes = document.nodes.filter((node) => !removedNodes.has(node.id)).map((node) => {
    if (node.kind !== "seedance") return node;
    const referenceBindings = node.referenceBindings.flatMap((binding): PromptBinding[] => {
      if (removedNodes.has(binding.source.nodeId) || (binding.source.inputEdgeId !== null && removedEdges.has(binding.source.inputEdgeId))) return [];
      if (binding.value.state !== "bound") return [binding];
      const previous = binding.value.target;
      const reason = removedNodes.has(previous.nodeId) ? "node_removed" : removedEdges.has(previous.edgeId) ? "edge_removed" : undefined;
      return [reason ? { ...binding, value: { state: "missing", reason, previous } } : binding];
    });
    return { ...node, referenceBindings };
  });
  return {
    ...document, nodes,
    edges: document.edges.filter((edge) => !removedEdges.has(edge.id)),
    groups: removedNodes.size === 0 ? document.groups : document.groups.map((group) => ({ ...group, nodeIds: group.nodeIds.filter((id) => !removedNodes.has(id)) })).filter((group) => group.nodeIds.length > 0)
  };
}

/** One complete immutable document operation; never rewrites ordinal text or authorizes assets. */
export function applyCanvasReferenceCommandV3(document: CanvasDocumentV3, command: CanvasReferenceCommandV3): CanvasDocumentV3 {
  canvasDocumentV3Schema.parse(document);
  let result: CanvasDocumentV3;
  switch (command.type) {
    case "delete_nodes": {
      const removed = new Set(command.nodeIds);
      if (!document.nodes.some((node) => removed.has(node.id))) return document;
      result = deleteGraph(document, removed, new Set());
      break;
    }
    case "delete_edges": {
      const removed = new Set(command.edgeIds);
      if (!document.edges.some((edge) => removed.has(edge.id))) return document;
      result = deleteGraph(document, new Set(), removed);
      break;
    }
    case "set_prompt":
      result = setPrompt(document, command.source, command.content);
      break;
    case "remove_occurrence": {
      const content = getPromptContent(document, command.source);
      if (!Number.isInteger(command.segmentIndex) || content.segments[command.segmentIndex]?.type !== "reference") throw new Error("要移除的引用出现位置无效。");
      result = setPrompt(document, command.source, { segments: content.segments.filter((_, index) => index !== command.segmentIndex) });
      break;
    }
    case "bind_reference":
    case "clear_binding": {
      const node = requireTarget(document, command.targetNodeId);
      const source = command.source;
      const input = document.edges.find((edge) => edge.id === source.inputEdgeId);
      if (source.inputEdgeId === null ? source.nodeId !== node.id || source.field !== "prompt" : input?.sourceNodeId !== source.nodeId || input.targetNodeId !== node.id) throw new Error("引用提示词上下文无效。");
      const segment = getPromptContent(document, source).segments.find((segment) => segment.type === "reference" && segment.slotId === command.slotId);
      if (segment?.type !== "reference") throw new Error("引用槽位不存在。");
      const index = node.referenceBindings.findIndex((binding) => binding.slotId === command.slotId && sameSource(binding.source, source));
      let value: PromptBinding["value"];
      if (command.type === "bind_reference") {
        const edge = document.edges.find((edge) => edge.id === command.mediaEdgeId);
        const media = document.nodes.find((node) => node.id === edge?.sourceNodeId);
        if (!edge || edge.targetNodeId !== node.id || !isMedia(media) || media.kind !== segment.kind) throw new Error("引用素材连线或媒体类型无效。");
        value = { state: "bound", target: { edgeId: edge.id, nodeId: media.id, assetId: media.assetId, kind: media.kind } };
      } else {
        const previous = previousIdentity(node.referenceBindings[index]);
        value = { state: "missing", reason: "unbound", ...(previous === undefined ? {} : { previous }) };
      }
      const binding = { source, slotId: command.slotId, value };
      const referenceBindings = [...node.referenceBindings];
      if (index < 0) referenceBindings.push(binding);
      else referenceBindings[index] = binding;
      result = { ...document, nodes: document.nodes.map((candidate) => candidate.id === node.id ? { ...node, referenceBindings } : candidate) };
      break;
    }
    case "replace_media": {
      const media = document.nodes.find((node) => node.id === command.nodeId);
      if (!isMedia(media)) throw new Error("要替换的素材节点不存在。");
      if (media.assetId === command.assetId) return document;
      result = { ...document, nodes: document.nodes.map((node) => {
        if (node.id === media.id) return { ...media, assetId: command.assetId };
        if (node.kind !== "seedance") return node;
        return { ...node, referenceBindings: node.referenceBindings.map((binding): PromptBinding =>
          binding.value.state === "bound" && binding.value.target.nodeId === media.id
            ? { ...binding, value: { state: "missing", reason: "asset_replaced", previous: binding.value.target } }
            : binding) };
      }) };
      break;
    }
    case "reorder_references": {
      requireTarget(document, command.targetNodeId);
      const nodes = new Map(document.nodes.map((node) => [node.id, node]));
      const incoming = document.edges.filter((edge) => edge.targetNodeId === command.targetNodeId)
        .sort((a, b) => a.order - b.order || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
      const mediaIds = new Set(incoming.filter((edge) => isMedia(nodes.get(edge.sourceNodeId))).map((edge) => edge.id));
      if (command.orderedEdgeIds.length !== mediaIds.size || new Set(command.orderedEdgeIds).size !== mediaIds.size || command.orderedEdgeIds.some((id) => !mediaIds.has(id))) throw new Error("引用排序必须完整包含当前目标的所有素材连线且不能重复。");
      let index = 0;
      const orders = new Map(incoming.map((edge, order) => [mediaIds.has(edge.id) ? command.orderedEdgeIds[index++]! : edge.id, order]));
      result = { ...document, edges: document.edges.map((edge) => orders.has(edge.id) ? { ...edge, order: orders.get(edge.id)! } : edge) };
      break;
    }
  }
  // Validation also makes a detached deep snapshot, including caller-supplied content.
  return canvasDocumentV3Schema.parse(result);
}
